/**
 * Phase 9.7 Q10 — 3-state provenance chip for pack content.
 *
 * CD R10 verdict Q10: every piece of content in the pack carries an origin
 * tag so the operator can see what they added vs what auto-attached vs what
 * they explicitly suppressed.
 *
 *   operator-added       — operator pinned this from a surface
 *   auto-attached        — Cultural Risk Layer / scenario pipeline pulled
 *                          this in automatically based on pathogen-class
 *                          match (wiring lands when Phase 9.5's auto-attach
 *                          pipeline does — schema typed today, palette
 *                          shipped today)
 *   operator-suppressed  — operator explicitly excluded this from the pack
 *                          (kept in queue, greyed-out, line-through)
 */

import type { ProvenanceOrigin } from './types';

const ORIGIN_META: Record<ProvenanceOrigin, { cssVar: string; bg: string; icon: string; label: string; title: string }> = {
  'operator-added': {
    cssVar: 'var(--bm-accent, #6ea3ff)',
    bg: 'var(--bm-accent-subtle, rgba(110,163,255,0.14))',
    icon: '📌',
    label: 'OP',
    title: 'Operator-added: you pinned this from a surface during this session.',
  },
  'auto-attached': {
    cssVar: 'var(--bm-prov-synthetic, var(--bm-blocked))',
    bg: 'var(--bm-prov-synthetic-bg, rgba(140,131,184,0.15))',
    icon: '◑',
    label: 'AUTO',
    title: 'Auto-attached: pulled in by the Cultural Risk Layer or scenario pipeline based on pathogen-class match.',
  },
  'operator-suppressed': {
    cssVar: 'var(--bm-text-subtle)',
    bg: 'transparent',
    icon: '✕',
    label: 'SUP',
    title: 'Operator-suppressed: you explicitly excluded this from the rendered pack (still in queue).',
  },
};

export function ProvenanceChip({ origin, small = false }: { origin: ProvenanceOrigin; small?: boolean }) {
  const m = ORIGIN_META[origin];
  return (
    <span
      title={m.title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: small ? '1px 5px' : '2px 7px',
        fontFamily: 'var(--bm-font-mono)', fontSize: small ? 9 : 9.5, fontWeight: 700,
        letterSpacing: '.3px',
        color: m.cssVar,
        background: m.bg,
        border: `1px solid ${m.cssVar}55`,
        borderRadius: 3, whiteSpace: 'nowrap' as const,
      }}
    >
      <span aria-hidden style={{ fontSize: small ? 8.5 : 9.5 }}>{m.icon}</span>
      {m.label}
    </span>
  );
}
